// src/scenes/DragController.js
import * as THREE from 'three';

export default class DragController {
  constructor({
    camera,
    canvas,
    controls,
    getDraggableMeshes,
    getFloorSize,
    getDraggedSize,
    getWallThickness,
    getSelectedMesh,
    onDragging,
    onDragEnd,
  }) {
    this.camera = camera;
    this.canvas = canvas;
    this.controls = controls;
    this.getDraggableMeshes = getDraggableMeshes;
    this.getFloorSize = getFloorSize;
    this.getDraggedSize = getDraggedSize;
    this.getWallThickness = getWallThickness;
    this.getSelectedMesh = getSelectedMesh;
    this.onDragging = onDragging;
    this.onDragEnd = onDragEnd;

    this.raycaster = new THREE.Raycaster();
    this.mouse = new THREE.Vector2();
    this.dragged = null;
    this.dragOffset = new THREE.Vector3();

    this.onMouseDown = this.onMouseDown.bind(this);
    this.onMouseMove = this.onMouseMove.bind(this);
    this.onMouseUp = this.onMouseUp.bind(this);

    this.canvas.addEventListener('mousedown', this.onMouseDown);
    this.canvas.addEventListener('mousemove', this.onMouseMove);
    this.canvas.addEventListener('mouseup', this.onMouseUp);
  }

  getNormalizedMouse(event) {
    const rect = this.canvas.getBoundingClientRect();
    return {
      x: ((event.clientX - rect.left) / rect.width) * 2 - 1,
      y: -((event.clientY - rect.top) / rect.height) * 2 + 1,
    };
  }

  onMouseDown = event => {
    const { x, y } = this.getNormalizedMouse(event);
    this.raycaster.setFromCamera({ x, y }, this.camera);

    const intersects = this.raycaster.intersectObjects(
      this.getDraggableMeshes()
    );
    if (intersects.length > 0) {
      const picked = intersects[0].object;
      const selected = this.getSelectedMesh?.();

      // Chỉ cho kéo nếu là mesh đã được chọn
      if (picked === selected) {
        this.controls.enabled = false;
        this.dragged = picked;

        const planeY = new THREE.Plane(
          new THREE.Vector3(0, 1, 0),
          -this.dragged.position.y
        );
        const intersection = new THREE.Vector3();
        this.raycaster.ray.intersectPlane(planeY, intersection);
        this.dragOffset.subVectors(this.dragged.position, intersection);
      }
    }
  };

  onMouseMove = event => {
    if (!this.dragged) return;

    const { x, y } = this.getNormalizedMouse(event);
    this.raycaster.setFromCamera({ x, y }, this.camera);

    const planeY = new THREE.Plane(
      new THREE.Vector3(0, 1, 0),
      -this.dragged.position.y
    );
    const intersection = new THREE.Vector3();
    this.raycaster.ray.intersectPlane(planeY, intersection);

    if (intersection) {
      const targetPos = intersection.add(this.dragOffset);

      // Lấy kích thước sàn và object kéo
      const { width, depth } = this.getFloorSize();
      const [colWidth, , colDepth] = this.getDraggedSize(this.dragged);

      const halfW = colWidth / 2;
      const halfD = colDepth / 2;

      // const wallThickness = this.getWallThickness();

      const minX = -width / 2 + halfW;
      const maxX = width / 2 - halfW;
      const minZ = -depth / 2 + halfD;
      const maxZ = depth / 2 - halfD;

      // Clamp (giới hạn) vị trí
      targetPos.x = Math.max(minX, Math.min(maxX, targetPos.x));
      targetPos.z = Math.max(minZ, Math.min(maxZ, targetPos.z));

      this.dragged.position.set(
        targetPos.x,
        this.dragged.position.y,
        targetPos.z
      );
    }
    if (this.onDragging) {
      this.onDragging(this.dragged);
    }
  };

  onMouseUp = () => {
    this.controls.enabled = true;
    this.dragged = null;
    if (this.onDragEnd) {
      this.onDragEnd();
    }
  };

  dispose() {
    this.canvas.removeEventListener('mousedown', this.onMouseDown);
    this.canvas.removeEventListener('mousemove', this.onMouseMove);
    this.canvas.removeEventListener('mouseup', this.onMouseUp);
  }
}
